import { type KeyboardEvent, type ReactNode, useCallback, useRef } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export interface PillOption {
    id: string;
    label: string;
    icon?: ReactNode;
    iconColor?: string;
}

export interface PillSelectorProps {
    /** The list of selectable pill options */
    options: PillOption[];
    /** Currently selected value(s) — a single string for radio mode, or string[] for multi-select */
    value: string | string[];
    /** Callback when selection changes */
    onChange: (value: string | string[]) => void;
    /** Enable multi-select mode (default: false = radio/single-select) */
    multiple?: boolean;
    /** Accessible label for the pill group */
    label?: string;
    /** Additional CSS class name */
    className?: string;
}

const containerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    gap: token("space.100")
});

const pillStateStyles = {
    unselected: css({
        backgroundColor: wacToken("color.neutral.light100"),
        border: "2px solid transparent"
    }),
    selected: css({
        backgroundColor: wacToken("color.extended.blue100"),
        border: `2px solid ${wacToken("color.primary.blue")}`
    })
};

const pillBaseStyles = css({
    "display": "inline-flex",
    "alignItems": "center",
    "gap": token("space.100"),
    "borderRadius": "20px",
    "padding": "6px 16px 6px 12px",
    "cursor": "pointer",
    "transition": "background-color 0.15s ease, border-color 0.15s ease",
    "userSelect": "none",
    "outline": "none",

    "&:focus-visible": {
        boxShadow: `0 0 0 2px ${wacToken(
            "color.neutral.light0"
        )}, 0 0 0 4px ${wacToken("color.primary.blue")}`
    },

    "&:hover": {
        backgroundColor: wacToken("color.extended.blue100")
    }
});

const pillTextUnselectedStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    color: wacToken("color.primary.black")
});

const pillTextSelectedStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    color: wacToken("color.primary.black")
});

const iconContainerStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "4px",
    flexShrink: 0
});

export const PillSelector = ({
    options,
    value,
    onChange,
    multiple = false,
    label = "Select an option",
    className
}: PillSelectorProps) => {
    const pillRefs = useRef<(HTMLDivElement | null)[]>([]);

    const isSelected = useCallback(
        (optionId: string): boolean => {
            if (Array.isArray(value)) {
                return value.includes(optionId);
            }
            return value === optionId;
        },
        [value]
    );

    const handleSelect = useCallback(
        (optionId: string) => {
            if (multiple) {
                const currentValue = Array.isArray(value)
                    ? value
                    : value
                    ? [value]
                    : [];
                const newValue = currentValue.includes(optionId)
                    ? currentValue.filter((id) => id !== optionId)
                    : [...currentValue, optionId];
                onChange(newValue);
            } else {
                onChange(optionId);
            }
        },
        [multiple, value, onChange]
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>, index: number) => {
            let targetIndex: number | null = null;

            switch (e.key) {
                case "ArrowRight":
                case "ArrowDown":
                    e.preventDefault();
                    targetIndex = (index + 1) % options.length;
                    break;
                case "ArrowLeft":
                case "ArrowUp":
                    e.preventDefault();
                    targetIndex = (index - 1 + options.length) % options.length;
                    break;
                case " ":
                case "Enter":
                    e.preventDefault();
                    const option = options[index];
                    if (option) {
                        handleSelect(option.id);
                    }
                    return;
                default:
                    return;
            }

            if (targetIndex !== null) {
                pillRefs.current[targetIndex]?.focus();
                if (!multiple) {
                    const target = options[targetIndex];
                    if (target) {
                        handleSelect(target.id);
                    }
                }
            }
        },
        [options, handleSelect, multiple]
    );

    const groupRole = multiple ? "group" : "radiogroup";
    const itemRole = multiple ? "checkbox" : "radio";

    return (
        <div
            css={containerStyles}
            role={groupRole}
            aria-label={label}
            className={className}
        >
            {options.map((option, index) => {
                const selected = isSelected(option.id);
                const stateKey = selected ? "selected" : "unselected";

                return (
                    <div
                        key={option.id}
                        ref={(el) => {
                            pillRefs.current[index] = el;
                        }}
                        css={[
                            pillBaseStyles,
                            pillStateStyles[stateKey],
                            selected
                                ? pillTextSelectedStyles
                                : pillTextUnselectedStyles
                        ]}
                        role={itemRole}
                        aria-checked={selected}
                        tabIndex={
                            multiple
                                ? 0
                                : selected ||
                                  (!Array.isArray(value) &&
                                      !value &&
                                      index === 0)
                                ? 0
                                : -1
                        }
                        onClick={() => handleSelect(option.id)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        data-testid={`pill-option-${option.id}`}
                    >
                        {option.icon && (
                            <span
                                css={iconContainerStyles}
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
                                style={{
                                    backgroundColor:
                                        option.iconColor ?? "transparent"
                                }}
                            >
                                {option.icon}
                            </span>
                        )}
                        <span>{option.label}</span>
                    </div>
                );
            })}
        </div>
    );
};
